import React, { createContext, useContext, useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react"; // Hook to interact with MSAL
import { loginRequest } from "config"; // Login request configuration
import { useNavigate } from "react-router-dom"; // For redirect after login if needed
import paths from "routes/paths";
import { env } from "config";
import { entraApi } from "services/api/entra";

export const AuthContext = createContext(); // Export AuthContext
export const useAuth = () => useContext(AuthContext); // Custom hook to access the authentication state

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null); // State to store the authenticated user's account information
  const [accessToken, setAccessToken] = useState(null); // State to store the API access token
  const [isLoading, setIsLoading] = useState(true); // Loading status
  const { instance, accounts, inProgress } = useMsal(); // MSAL instance and accounts from MSAL
  const navigate = useNavigate();
  const [companyList, setCompanyList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    const checkAuth = async () => {
      if (inProgress === "none") {
        if (accounts.length > 0) {
          const currentAccount = accounts[0];
          setAccount(currentAccount);
          const token = await fetchAccessToken(currentAccount);
          if (token) {
            setIsLoading(true);
            const api = entraApi(token);
            const [groups, users, availableModules] = await Promise.all([
              api.getGroups(),
              api.getUsers(),
              api.getModules(),
            ]);
            setCompanyList(groups || []);
            setUserList(users || []);
            setModules(availableModules || []);
            setIsLoading(false);
          }
        } else {
          setAccount(null);
        }
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [accounts, inProgress]);

  // Multi-tab synchronization: listen to MSAL localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && e.key.includes("msal")) {
        setTimeout(async () => {
          const currentAccounts = instance.getAllAccounts();
          if (currentAccounts.length === 0 && account) {
            setAccount(null);
            setAccessToken(null);
            setCompanyList([]);
            setUserList([]);
            setModules([]);
            navigate(paths.login, { replace: true });
          }
        }, 100);
      }
    };

    // Listen to localStorage changes
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [account, instance, navigate]);

  const login = async () => {
    try {
      setIsLoading(true);
      const response = await instance.loginPopup(loginRequest);
      setAccount(response.account);
      await fetchAccessToken(response.account);
    } catch (error) {
      console.error("Login failed: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);

      // Clear MSAL cache first
      await instance.clearCache();

      // Trigger logout using popup
      await instance.logoutPopup();

      // Clear all local states
      setAccount(null);
      setAccessToken(null);
      setCompanyList([]);
      setUserList([]);
      setModules([]);

      // Navigate to login page after successful logout
      navigate(paths.login);
    } catch (error) {
      console.error("Logout failed: ", error);
      // Even if logout fails, clear local states
      setAccount(null);
      setAccessToken(null);
      setCompanyList([]);
      setUserList([]);
      setModules([]);
      navigate(paths.login);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch the access token from MSAL
  const fetchAccessToken = async (account) => {
    try {
      setIsLoading(true);
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error("Token acquisition failed:", error);
      if (
        error instanceof Error &&
        error.message.includes("interaction_required")
      ) {
        const response = await instance.acquireTokenPopup({
          ...loginRequest,
          account: account,
        });
        setAccessToken(response.accessToken);
        return response.accessToken;
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const ensureValidToken = async () => {
    if (!account) {
      navigate(paths.login, { replace: true });
      throw new Error("No authenticated account available");
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      if (response.accessToken !== accessToken) {
        setAccessToken(response.accessToken);
      }

      return response.accessToken;
    } catch (error) {
      console.error("Silent token acquisition failed:", error);

      setAccount(null);
      setAccessToken(null);
      setCompanyList([]);
      setUserList([]);
      setModules([]);

      navigate(paths.login, { replace: true });
      throw new Error("Authentication required - redirected to login");
    }
  };

  const hasModule = (moduleName) => {
    if (!modules || modules.length === 0) return false;
    return modules.some((module) => {
      return module?.toLowerCase() === moduleName.toLowerCase();
    });
  };

  return (
    <AuthContext.Provider
      value={{
        account,
        login,
        logout,
        accessToken,
        isLoading,
        companyList,
        userList,
        modules,
        hasModule,
        ensureValidToken,
      }}
    >
      {children} {/* Render children (wrapped components) */}
    </AuthContext.Provider>
  );
};
